import React, { useContext, useState } from "react";
import {
  Avatar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
  Button,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { Plus, Users } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import UserStatusIcon from "../UserModal/statusIcon";
import { ChatModal } from "../../pages/Chat";

const InternalChatCreateControls = ({
  controlButtonClassName,
  onChatCreated,
}) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const [createChatOpen, setCreateChatOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [chatsCache, setChatsCache] = useState([]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const { data } = await api.get("/users/list");
      setAvailableUsers(
        (data || []).filter((u) => u.id !== loggedInUser.id)
      );
    } catch (err) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadChatsCache = async () => {
    try {
      const { data } = await api.get("/chats");
      setChatsCache(Array.isArray(data?.records) ? data.records : []);
    } catch (err) {
      setChatsCache([]);
    }
  };

  const openCreateChat = async () => {
    setCreateChatOpen(true);
    setUserSearchTerm("");
    await Promise.all([loadUsers(), loadChatsCache()]);
  };

  const openCreateGroup = async () => {
    await loadChatsCache();
    setCreateGroupOpen(true);
  };

  const handleCreateChatWithUser = async (selectedUser) => {
    try {
      const existingChat = chatsCache.find((c) => {
        const hasSelectedUser = c.users?.some((u) => u.userId === selectedUser.id);
        const hasLoggedInUser = c.users?.some((u) => u.userId === loggedInUser.id);
        const isIndividualChat = !c.isGroup && c.users?.length === 2;
        return isIndividualChat && hasSelectedUser && hasLoggedInUser;
      });

      if (existingChat) {
        toast.info("Chat com este usuário já existe!");
        if (onChatCreated) onChatCreated(existingChat);
        setCreateChatOpen(false);
        return;
      }

      const { data } = await api.post("/chats", {
        users: [{ id: selectedUser.id }],
        isGroup: false,
      });

      toast.success("Chat criado com sucesso!");
      setCreateChatOpen(false);
      if (onChatCreated) onChatCreated(data);
    } catch (err) {
      toast.error("Erro ao criar chat");
    }
  };

  const filteredUsers = availableUsers.filter((u) =>
    String(u.name || "")
      .toLowerCase()
      .includes(userSearchTerm.toLowerCase())
  );

  return (
    <>
      <Tooltip title="Nova conversa" placement="top" arrow>
        <div className={controlButtonClassName} onClick={openCreateChat}>
          <Plus size={18} />
        </div>
      </Tooltip>

      <Tooltip title="Criar grupo" placement="top" arrow>
        <div className={controlButtonClassName} onClick={openCreateGroup}>
          <Users size={18} />
        </div>
      </Tooltip>

      <Dialog
        open={createChatOpen}
        onClose={() => setCreateChatOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("chatList.createNewChat") || "Nova conversa"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder={i18n.t("chatList.searchUser") || "Buscar usuário"}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            style={{ marginBottom: 16, marginTop: 8 }}
            InputProps={{
              startAdornment: (
                <SearchIcon style={{ marginRight: 8, color: "#666" }} />
              ),
            }}
          />
          {usersLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <List style={{ maxHeight: 400, overflow: "auto" }}>
              {filteredUsers.map((u) => (
                <ListItem
                  key={u.id}
                  button
                  onClick={() => handleCreateChatWithUser(u)}
                  style={{ borderRadius: 8, marginBottom: 4 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={
                        u?.profileImage?.startsWith("http")
                          ? u.profileImage
                          : u?.profileImage
                          ? `${getBackendUrl()}/public/company${u.companyId}/user/${u.profileImage}`
                          : undefined
                      }
                    >
                      {(u.name || "?").charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Typography variant="subtitle1">{u.name}</Typography>
                        <UserStatusIcon user={u} />
                      </div>
                    }
                    secondary={u.email}
                  />
                </ListItem>
              ))}
              {filteredUsers.length === 0 && !usersLoading && (
                <Typography
                  align="center"
                  color="textSecondary"
                  style={{ padding: 20 }}
                >
                  Nenhum usuário encontrado
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateChatOpen(false)} color="primary">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <ChatModal
        type="new"
        open={createGroupOpen}
        chat={{}}
        handleClose={() => setCreateGroupOpen(false)}
        handleLoadNewChat={(chat) => {
          if (onChatCreated) onChatCreated(chat);
        }}
        findChats={async () => {
          const { data } = await api.get("/chats");
          return data;
        }}
        setChats={() => {}}
        setChatsPageInfo={() => {}}
        chats={chatsCache}
        loggedInUserId={loggedInUser.id}
      />
    </>
  );
};

export default InternalChatCreateControls;
